import { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import clsx from "clsx";

import styles from "./barcode.module.css";

export type BarcodeFormat = "CODE128" | "CODE39";

interface BarcodeProps {
  value: string;
  /** Accessible name; falls back to the encoded value. */
  label?: string;
  format?: BarcodeFormat;
  /** Bar height in px. The rendered SVG scales with its container width. */
  height?: number;
  /** Render the human-readable value beneath the bars. */
  displayValue?: boolean;
  className?: string;
}

/**
 * Renders `value` as a scannable barcode.
 *
 * Bar and background colours are passed to JsBarcode as options, which lands
 * them as SVG attributes rather than CSS. That keeps the dark-on-light contrast
 * scanners need even under the dark theme, where `--pallet-color-white` resolves
 * to a near-black.
 *
 * The <svg> stays mounted even when `value` is empty or cannot be encoded (it is
 * collapsed via `data-barcode-state` instead), so a later switch back to an
 * encodable value still has a node to render into.
 */
function Barcode({
  value,
  label,
  format = "CODE128",
  height = 44,
  displayValue = false,
  className,
}: BarcodeProps) {
  const ref = useRef<SVGSVGElement>(null);
  const [encodable, setEncodable] = useState(true);

  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;

    if (!value) {
      svg.replaceChildren();
      setEncodable(true);
      return;
    }

    JsBarcode(svg, value, {
      format,
      height,
      displayValue,
      // Quiet zone. Scanners need clear space either side of the symbol; the
      // background rect extends underneath it.
      margin: 10,
      width: 2,
      background: "#ffffff",
      lineColor: "#000000",
      valid: (isValid) => {
        setEncodable(isValid);
        if (!isValid) svg.replaceChildren();
      },
    });
  }, [value, format, height, displayValue]);

  const hidden = !value || !encodable;

  return (
    <svg
      ref={ref}
      role="img"
      aria-label={label ?? value}
      aria-hidden={hidden || undefined}
      data-test-role="barcode"
      data-barcode-state={hidden ? "hidden" : "rendered"}
      data-barcode-value={value}
      className={clsx(styles.barcode, className)}
    />
  );
}

export default Barcode;
